/* eslint-disable no-unused-vars */
import styled, { keyframes } from 'styled-components';
import { Palette } from 'config';
import { Link as RouterLink } from 'react-router-dom';

const shine = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

export const BtnBox = styled.div`
  position: relative;
  margin-bottom: 24px;

  button {
    width: 100%;
    height: auto !important;
    padding: 12px 0 !important;
    background: linear-gradient(to right, #3599e7, #9accf3) !important;
    color: white !important;
    border-radius: 12px !important;
    font-weight: 600 !important;
    border: none !important;
    box-shadow: 0 10px 15px -3px rgba(53, 153, 231, 0.3) !important;
    transition: transform 0.2s ease;
    font-family: 'Lato', system-ui, sans-serif;
    overflow: hidden;
    position: relative;
  }

  button::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to right, rgba(255, 255, 255, 0.3), transparent);
    animation: ${shine} 2.5s linear infinite;
    pointer-events: none;
  }

  button:hover:not(:disabled) {
    transform: scale(1.02);
  }
  button:active:not(:disabled) {
    transform: scale(0.98);
  }
`;

export const Box = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  position: absolute;
  top: 0;
  left: 0;
  background-color: #060429;
  overflow: hidden;
`;

export const FlexCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

export const Label = styled.p`
  font-size: 40px;
  font-weight: 700;
  line-height: 48px;
  font-family: 'Lato', system-ui, sans-serif;
  background: linear-gradient(135deg, #a4cbef 0%, #5397e1 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  padding-bottom: 4px;
  margin: 0;
`;

export const SeparatorGradient = styled.div`
  height: 1px;
  background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.2), transparent);
  margin-bottom: 24px;
`;

export const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 440px;
  margin: 0 16px;
  position: relative;
  z-index: 10;

  /* Glassmorphism */
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  padding: 32px;
  @media (min-width: 768px) {
    padding: 40px;
  }
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  box-sizing: border-box;

  /* Form Glow Effect */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to right, rgba(53, 153, 231, 0.2), rgba(46, 185, 167, 0.2));
    border-radius: 24px;
    filter: blur(40px);
    z-index: -1;
  }

  & .MuiInputBase-root {
    background: rgba(255, 255, 255, 0.05) !important;
    border: 2px solid rgba(255, 255, 255, 0.2) !important;
    backdrop-filter: blur(4px);
    border-radius: 12px !important;
    color: #fff !important;
    transition: all 0.3s ease;
    font-family: 'Lato', system-ui, sans-serif;
    input {
      color: #fff !important;
      &:-webkit-autofill,
      &:-webkit-autofill:hover,
      &:-webkit-autofill:focus,
      &:-webkit-autofill:active {
        -webkit-text-fill-color: #fff !important;
        -webkit-box-shadow: 0 0 0px 1000px transparent inset !important;
        transition: background-color 5000s ease-in-out 0s;
      }
    }
    input::placeholder {
      color: rgba(255, 255, 255, 0.3) !important;
      opacity: 1 !important;
    }
    svg {
      fill: rgba(255, 255, 255, 0.4) !important;
    }
  }

  & .MuiInputBase-root.Mui-focused {
    border: 2px solid #3599e7 !important;
    box-shadow: none !important;
  }
`;

export const Block = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

export const Paragraph = styled.p`
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  color: #a0aec0;
`;

export const Link = styled(RouterLink)`
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  color: #3599e7;
  text-decoration: none;
`;

export const SignUpLink = styled(Link)`
  font-weight: 700;
`;

export const Footer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
`;

export const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  width: 100%;
`;

export const InputLabel = styled.label`
  font-size: 14px;
  color: #a0aec0;
  margin-bottom: 8px;
  font-family: 'Lato', system-ui, sans-serif;
  font-weight: 500;
`;

export const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid ${(props) => (props.error ? '#ff4d4f' : 'rgba(255, 255, 255, 0.2)')};
  border-radius: 12px;
  padding: 0 16px;
  height: 48px;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  box-sizing: border-box;

  &:focus-within {
    border-color: ${(props) => (props.error ? '#ff4d4f' : '#3599e7')};
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(53, 153, 231, 0.15);
  }
`;

export const StyledInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #fff;
  font-size: 15px;
  font-family: 'Lato', system-ui, sans-serif;
  height: 100%;
  width: 100%;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-text-fill-color: #fff !important;
    -webkit-box-shadow: 0 0 0px 1000px transparent inset !important;
    transition: background-color 5000s ease-in-out 0s;
  }
`;

export const InputIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.4);
  margin-left: 8px;
  user-select: none;
  transition: color 0.2s ease;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const ErrorText = styled.span`
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
  font-family: 'Lato', system-ui, sans-serif;
`;

export const TextButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #3599e7;
  font-family: 'Lato', system-ui, sans-serif;

  &:disabled {
    color: #718096;
    cursor: not-allowed;
  }
`;
